import { useEffect, useState } from 'react';
import { BarChart3, TrendingUp, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { getOutreachStatus, getOutreachLogs } from '../../lib/outreach-api';

function pct(n, d) {
  if (!d) return '0%';
  return `${Math.round((n / d) * 100)}%`;
}

export const OutreachAnalyticsPage = () => {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, l] = await Promise.all([
          getOutreachStatus(),
          getOutreachLogs(100),
        ]);
        setStatus(s.data);
        setLogs(Array.isArray(l.data) ? l.data : []);
      } catch (e) {
        // best-effort
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const total = status?.total || { sent: 0, replies: 0 };
  const today = status?.today || { sent: 0, replies: 0 };
  const replyRate = pct(total.replies, total.sent);

  // Group logs by intent for a quick distribution view
  const intentCounts = logs.reduce((acc, l) => {
    acc[l.intent] = (acc[l.intent] || 0) + 1;
    return acc;
  }, {});
  const intentEntries = Object.entries(intentCounts).sort((a, b) => b[1] - a[1]);

  // Reply status distribution
  const statusCounts = logs.reduce((acc, l) => {
    acc[l.status] = (acc[l.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="outreach-analytics">
      <header>
        <div className="flex items-center gap-2 mb-1">
          <BarChart3 className="w-4 h-4 text-amber-400" />
          <span className="text-xs uppercase tracking-widest text-amber-400 font-semibold">
            Outreach AI · Analytics
          </span>
        </div>
        <h1 className="text-3xl font-semibold tracking-tight">Analytics</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Outbound volume, reply rate, and intent distribution for the Outreach Agent.
        </p>
      </header>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-grid">
        {[
          { label: 'Sent today', value: today.sent },
          { label: 'Replies today', value: today.replies },
          { label: 'Total sent', value: total.sent },
          { label: 'Reply rate', value: replyRate, accent: 'text-amber-400' },
        ].map((k) => (
          <Card key={k.label} className="bg-card/40 backdrop-blur">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">{k.label}</span>
                <TrendingUp className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
              <span className={`text-2xl font-semibold tracking-tight ${k.accent || ''}`}>
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (k.value ?? '–')}
              </span>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Intent distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Intent distribution (last 100 messages)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {loading && <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />}
          {!loading && intentEntries.length === 0 && (
            <p className="text-sm text-muted-foreground">No data yet.</p>
          )}
          {!loading && intentEntries.map(([intent, count]) => {
            const maxCount = intentEntries[0][1];
            const width = (count / maxCount) * 100;
            return (
              <div key={intent} data-testid={`intent-row-${intent}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-medium">{intent}</span>
                  <span className="text-muted-foreground">{count}</span>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full bg-amber-500/70 rounded-full transition-all"
                    style={{ width: `${width}%` }}
                  />
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Status distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Message status (last 100)</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {loading && <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />}
          {!loading && Object.keys(statusCounts).length === 0 && (
            <p className="text-sm text-muted-foreground">No data yet.</p>
          )}
          {!loading && Object.entries(statusCounts).map(([s, c]) => (
            <Badge
              key={s}
              variant="outline"
              className="text-xs"
              data-testid={`status-${s}`}
            >
              {s}: {c}
            </Badge>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default OutreachAnalyticsPage;
